import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useRecipes } from './useRecipes'
import { RecipeCard } from './RecipeCard'
import type { Recipe } from './types'

interface RecipeListProps {
  recipes?: Recipe[]
  isLoading?: boolean
  searchQuery?: string
  onClearSearch?: () => void
}

export function RecipeList({ recipes: externalRecipes, isLoading: externalLoading, searchQuery, onClearSearch }: RecipeListProps = {}) {
  const { t } = useTranslation('recipes')
  const { data: fetchedRecipes, isLoading: fetchLoading, isError } = useRecipes()

  const recipes = externalRecipes ?? fetchedRecipes
  const isLoading = externalLoading ?? fetchLoading

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-surface animate-pulse" />
        ))}
      </div>
    )
  }

  if (isError && !externalRecipes) {
    return (
      <p className="text-danger text-sm" role="alert">
        {t('list.error')}
      </p>
    )
  }

  if (!recipes || recipes.length === 0) {
    if (searchQuery) {
      return (
        <div className="text-center py-12">
          <p className="text-muted text-sm mb-4">
            {t('search.noResults', { query: searchQuery })}
          </p>
          {onClearSearch && (
            <button
              onClick={onClearSearch}
              className="text-accent text-sm font-medium underline"
            >
              {t('search.clearSearch')}
            </button>
          )}
        </div>
      )
    }
    return (
      <div className="text-center py-12">
        <p className="text-muted text-sm mb-4">{t('list.empty')}</p>
        <Link
          to="/templates"
          className="text-accent text-sm font-medium underline"
        >
          {t('list.browseTemplates')}
        </Link>
      </div>
    )
  }

  const favourites = [...recipes.filter((r) => r.isFavourite)].sort((a, b) =>
    a.name.localeCompare(b.name),
  )
  const others = [...recipes.filter((r) => !r.isFavourite)].sort((a, b) =>
    a.name.localeCompare(b.name),
  )

  return (
    <div className="space-y-6">
      {favourites.length > 0 && (
        <section aria-label={t('list.favouritesSection')}>
          <h2 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
            {t('list.favouritesSection')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {favourites.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </section>
      )}
      {others.length > 0 && (
        <section aria-label={t('list.allRecipesSection')}>
          <h2 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
            {t('list.allRecipesSection')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
